/**
 * Import half of Settings → Backup & Transfer → Connections.
 *
 * Two steps, and the first one writes nothing. Picking a file only reads and
 * validates it; the list that comes back is the user's chance to see every
 * endpoint — and every SSH bastion — before agreeing to save it. That review
 * is the load-bearing control here, so the panel never auto-ticks a row it
 * thinks is fine and never hides a record it refused: skipped entries are
 * reported with a reason.
 *
 * A wrong passphrase is reported and nothing is imported — main verifies it
 * before building a single record — so the user can simply retype and retry
 * with their selection intact.
 */
import { useState } from 'react';
import { FileUp, Upload } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from '../../../components/ui/toaster';
import { useAnalytics } from '../../../core/analytics/useAnalytics';
import type { ConnectionPreviewRow } from '../../../types/connectionTransfer';
import { useConnectionStore } from '../store/connectionStore';
import { countBucket } from '../connection-import/connectionImportUtils';
import { applyConnectionsImport, previewConnectionsFile } from './connectionTransferApi';
import { ConnectionPickList, type PickRow } from './ConnectionPickList';
import { ImportPassphraseField, ImportSkippedList } from './ImportReviewNotes';
import { importToastDescription } from './transferToastText';
import { safeServerText } from '../../../types/safeConnectionDisplay';

interface Loaded {
  filePath: string;
  rows: ConnectionPreviewRow[];
  skipped: { index: number; reason: string }[];
  hasSecrets: boolean;
}

function toPickRow(row: ConnectionPreviewRow): PickRow {
  const pick: PickRow = {
    id: row.exportId,
    label: safeServerText(row.alias),
    target: row.target,
    detail: [row.engine, row.authentication, row.database, row.login]
      .filter(Boolean)
      .join(' · '),
  };
  if (row.sshTarget) pick.sshTarget = row.sshTarget;
  if (row.transport) pick.transport = row.transport;
  if (row.transportWeak) pick.transportWeak = true;
  const notes = [row.duplicate ? 'already saved' : '', row.hadPassword ? 'had a password' : '']
    .filter(Boolean)
    .join(' · ');
  if (notes) pick.note = notes;
  return pick;
}

export function ConnectionsImportPanel() {
  const { track } = useAnalytics();
  const loadConnections = useConnectionStore((s) => s.loadConnections);
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [passphrase, setPassphrase] = useState('');
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setLoaded(null);
    setSelected(new Set());
    setPassphrase('');
  };

  const onChooseFile = async () => {
    setBusy(true);
    try {
      const preview = await previewConnectionsFile();
      if (!preview.success) {
        if (preview.cancelled) return;
        reset();
        toast.error({ title: 'Could not open that file', description: preview.error });
        return;
      }
      setLoaded({
        filePath: preview.filePath,
        rows: preview.rows,
        skipped: preview.skipped,
        hasSecrets: preview.hasSecrets,
      });
      // Anything already saved starts unticked; a re-run of the same import
      // should not quietly create a second copy of everything.
      setSelected(new Set(preview.rows.filter((r) => !r.duplicate).map((r) => r.exportId)));
      setPassphrase('');
    } finally {
      setBusy(false);
    }
  };

  const onImport = async () => {
    if (!loaded) return;
    setBusy(true);
    try {
      const result = await applyConnectionsImport({
        filePath: loaded.filePath,
        exportIds: [...selected],
        ...(passphrase ? { passphrase } : {}),
      });
      if (!result.success) {
        // Selection and file stay put so a mistyped passphrase is one retry.
        toast.error({
          title: result.badPassphrase ? 'That passphrase did not work' : 'Nothing was imported',
          description: result.error,
        });
        return;
      }
      track('feature:connections_import', {
        with_secrets: result.secretsRestored > 0,
        count_bucket: countBucket(result.imported),
      });
      await loadConnections();
      toast.success({
        title: `Imported ${result.imported} connection${result.imported === 1 ? '' : 's'}`,
        description: importToastDescription(result),
      });
      reset();
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="space-y-3" data-testid="connections-import-panel">
      <div className="text-sm font-medium">Import connections</div>
      <p className="text-xs text-muted-foreground">
        Imported connections are added, never merged over existing ones, and nothing connects
        automatically. Check where each one points before you tick it.
      </p>

      <Button
        variant="outline"
        onClick={onChooseFile}
        disabled={busy}
        data-testid="connections-import-choose"
        className="gap-2"
      >
        <FileUp className="h-4 w-4" />
        Choose connections file…
      </Button>

      {loaded ? (
        <div className="space-y-3">
          <ConnectionPickList
            rows={loaded.rows.map(toPickRow)}
            selected={selected}
            onToggle={(id, next) =>
              setSelected((current) => {
                const copy = new Set(current);
                if (next) copy.add(id);
                else copy.delete(id);
                return copy;
              })
            }
            idPrefix="connections-import"
            emptyLabel="That file has no connections this version can import."
          />

          <ImportSkippedList skipped={loaded.skipped} />

          {loaded.hasSecrets ? (
            <ImportPassphraseField value={passphrase} onChange={setPassphrase} />
          ) : null}

          <Button
            onClick={onImport}
            disabled={busy || selected.size === 0}
            data-testid="connections-import-button"
            className="gap-2"
          >
            <Upload className="h-4 w-4" />
            {busy ? 'Importing…' : `Import ${selected.size} connection${selected.size === 1 ? '' : 's'}`}
          </Button>
        </div>
      ) : null}
    </section>
  );
}
